import React, { useState } from 'react';
import DragDropList from '../shared/drag_drop_list';
import TrainingItemBuilder from './training_item_builder';

const TrainingModuleBuilder = ({ initialModule, isEdit = false }) => {
  const [module, setModule] = useState(
    initialModule || {
      name: '',
      description: '',
      passing_score: 80,
      training_items: []
    }
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  const handleModuleChange = (field, value) => {
    setModule(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleItemChange = (itemIndex, updatedItem) => {
    setModule(prev => ({
      ...prev,
      training_items: prev.training_items.map((item, index) =>
        index === itemIndex ? { ...updatedItem, position: index + 1 } : item
      )
    }));
  };

  const handleItemDelete = async (itemIndex) => {
    const item = module.training_items[itemIndex];
    const itemType = item.type === 'TrainingVideo' ? 'video' : 'question';

    const result = await swal({
      title: 'Are you sure?',
      text: `Do you want to delete this ${itemType}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, delete the ${itemType}`,
      cancelButtonText: 'No, keep it'
    });

    if (result.value) {
      // If item has an id and we're editing an existing module, delete from server
      if (isEdit && item.id && !item.id.toString().startsWith('new_')) {
        try {
          const response = await fetch(`/training_modules/${module.id}/training_items/${item.id}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            // Remove from local state immediately
            setModule(prev => ({
              ...prev,
              training_items: prev.training_items
                .filter((_, index) => index !== itemIndex)
                .map((item, index) => ({ ...item, position: index + 1 }))
            }));
          } else {
            swal('Error', 'Failed to delete item', 'error');
          }
        } catch (error) {
          console.error('Delete error:', error);
          swal('Error', 'Failed to delete item', 'error');
        }
      } else {
        // Just remove from local state (not saved yet or new module)
        setModule(prev => ({
          ...prev,
          training_items: prev.training_items
            .filter((_, index) => index !== itemIndex)
            .map((item, index) => ({ ...item, position: index + 1 }))
        }));
      }
    }
  };

  const handleItemsReorder = (reorderedItems) => {
    const itemsWithPositions = reorderedItems.map((item, index) => ({
      ...item,
      position: index + 1
    }));

    setModule(prev => ({
      ...prev,
      training_items: itemsWithPositions
    }));

    // Save new order to server only if editing existing module with saved items
    if (isEdit && module.id) {
      const itemIds = itemsWithPositions
        .map(item => item.id)
        .filter(id => id && !id.toString().startsWith('new_'));

      if (itemIds.length > 0) {
        fetch(`/training_modules/${module.id}/training_items/reorder`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ order: itemIds })
        });
      }
    }
  };

  const addVideo = () => {
    setEditingItem(null);
    setEditingItemIndex(null);
    setShowAddVideoModal(true);
  };

  const addQuestion = () => {
    setEditingItem(null);
    setEditingItemIndex(null);
    setShowAddQuestionModal(true);
  };

  const handleItemEdit = (itemIndex) => {
    const item = module.training_items[itemIndex];
    setEditingItem(item);
    setEditingItemIndex(itemIndex);
    if (item.type === 'TrainingVideo') {
      setShowAddVideoModal(true);
    } else {
      setShowAddQuestionModal(true);
    }
  };

  const handleAddItem = (newItem) => {
    // Add item to local state with a temporary ID
    const tempId = `new_${Date.now()}`;
    setModule(prev => ({
      ...prev,
      training_items: [
        ...prev.training_items,
        { ...newItem, id: tempId, position: prev.training_items.length + 1 }
      ]
    }));
    setShowAddVideoModal(false);
    setShowAddQuestionModal(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!module.name || module.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    if (!module.passing_score || module.passing_score < 0 || module.passing_score > 100) {
      newErrors.passing_score = 'Passing score must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const url = isEdit
        ? `/training_modules/${module.id}`
        : '/training_modules';

      const method = isEdit ? 'PATCH' : 'POST';

      // Prepare request body
      const requestBody = {
        training_module: {
          name: module.name,
          description: module.description,
          passing_score: module.passing_score
        }
      };

      // For new modules, include the training items
      if (!isEdit && module.training_items.length > 0) {
        requestBody.training_items = module.training_items.map((item, index) => ({
          type: item.type,
          title: item.title,
          content: item.content,
          video_url: item.video_url,
          length: item.length,
          question_type: item.question_type,
          options: item.options,
          correct_answer: item.correct_answer,
          points: item.points,
          position: index + 1
        }));
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = `/training_modules/${data.id || module.id}`;
      } else {
        const data = await response.json();
        setErrors(data.errors || { base: 'An error occurred' });
      }
    } catch (error) {
      setErrors({ base: 'An error occurred while saving' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="training-module-builder">
      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header bg-training text-white">
            <h5 className="mb-0">
              <i className="fa fa-video-camera mr-2"></i>
              {isEdit ? 'Edit Training Module' : 'New Training Module'}
            </h5>
          </div>
          <div className="card-body">
            <h6>Module Settings</h6>

            {errors.base && (
              <div className="alert alert-danger">{errors.base}</div>
            )}

            <div className="form-group">
              <label>Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                value={module.name}
                onChange={(e) => handleModuleChange('name', e.target.value)}
                required
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={module.description}
                onChange={(e) => handleModuleChange('description', e.target.value)}
              />
              <small className="form-text text-muted">Brief description of this training module</small>
            </div>

            <div className="form-group">
              <label>Passing Score (%) <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-control ${errors.passing_score ? 'is-invalid' : ''}`}
                min="0"
                max="100"
                value={module.passing_score}
                onChange={(e) => handleModuleChange('passing_score', parseInt(e.target.value))}
                required
              />
              <small className="form-text text-muted">Percentage score required to pass (default: 80%)</small>
              {errors.passing_score && <div className="invalid-feedback">{errors.passing_score}</div>}
            </div>

            <hr />

            <div className="mb-3">
              <h6 className="mb-0">Training Content</h6>
            </div>

            {module.training_items && module.training_items.length > 0 ? (
              <>
                <DragDropList
                  items={module.training_items}
                  onReorder={handleItemsReorder}
                  renderItem={(item, index) => (
                    <TrainingItemBuilder
                      item={item}
                      index={index}
                      onChange={handleItemChange}
                      onDelete={handleItemDelete}
                      onEdit={handleItemEdit}
                      moduleId={module.id}
                    />
                  )}
                />
                <p className="text-muted mt-3 mb-0">
                  <small><i className="fa fa-info-circle"></i> Drag items to reorder them. Videos and questions will be presented in this order during training.</small>
                </p>

                {/* Add buttons below existing items */}
                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary mr-2"
                    onClick={addVideo}
                  >
                    <i className="fa fa-plus"></i> Add Video
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={addQuestion}
                  >
                    <i className="fa fa-plus"></i> Add Question
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state text-center py-4">
                <i className="fa fa-film fa-3x text-muted mb-3"></i>
                <h6 className="text-muted">No training content yet</h6>
                <p className="text-muted">Add videos and questions to build your training module.</p>

                <button
                  type="button"
                  className="btn btn-outline-primary mr-2"
                  onClick={addVideo}
                >
                  <i className="fa fa-plus"></i> Add First Video
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={addQuestion}
                >
                  <i className="fa fa-plus"></i> Add First Question
                </button>
              </div>
            )}
          </div>

          <div className="card-body">
            <div className="d-flex justify-content-between">
              <div>
                {isEdit && module.id && (
                  <a
                    href={`/training_modules/${module.id}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-info"
                  >
                    <i className="fa fa-eye"></i> Preview
                  </a>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  className="btn btn-primary mr-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <i className="fa fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save"></i> Save
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-default"
                  onClick={() => {
                    if (confirm('Are you sure? You may have unsaved changes.')) {
                      window.location.href = '/training_modules';
                    }
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <style>{`
        .empty-state {
          border: 2px dashed #dee2e6;
          border-radius: 0.25rem;
          margin: 1rem 0;
        }
      `}</style>

      {showAddVideoModal && (
        <VideoModal
          onClose={() => {
            setShowAddVideoModal(false);
            setEditingItem(null);
            setEditingItemIndex(null);
          }}
          onAdd={handleAddItem}
          moduleId={module.id}
          isEdit={isEdit}
          editingItem={editingItem}
        />
      )}

      {showAddQuestionModal && (
        <QuestionModal
          onClose={() => {
            setShowAddQuestionModal(false);
            setEditingItem(null);
            setEditingItemIndex(null);
          }}
          onAdd={handleAddItem}
          moduleId={module.id}
          isEdit={isEdit}
          editingItem={editingItem}
        />
      )}
    </div>
  );
};

const VideoModal = ({ onClose, onAdd, moduleId, isEdit, editingItem }) => {
  const isEditing = !!editingItem;
  const [video, setVideo] = useState({
    type: 'TrainingVideo',
    title: editingItem?.title || '',
    video_url: editingItem?.video_url || '',
    length: editingItem?.length || '',
    content: editingItem?.content || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSaving(true);

    try {
      // If editing an existing module, save to server immediately
      if (isEdit && moduleId) {
        const url = isEditing
          ? `/training_modules/${moduleId}/training_items/${editingItem.id}`
          : `/training_modules/${moduleId}/training_items`;
        const method = isEditing ? 'PATCH' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ training_item: video })
        });

        if (response.ok) {
          window.location.reload();
        } else {
          const errorData = await response.text();
          console.error('Update failed:', response.status, errorData);
          swal('Error', `Failed to ${isEditing ? 'update' : 'add'} video`, 'error');
        }
      } else {
        // For new modules, just add to local state
        onAdd(video);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      swal('Error', `Error ${isEditing ? 'updating' : 'adding'} video`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditing ? 'Edit Video' : 'Add Video'}</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={video.title}
                  onChange={(e) => setVideo({ ...video, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Video URL <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={video.video_url}
                  onChange={(e) => setVideo({ ...video, video_url: e.target.value })}
                  required
                />
                <small className="form-text text-muted">
                  YouTube embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)<br/>
                  or Vimeo embed URL (e.g., https://player.vimeo.com/video/VIDEO_ID)
                </small>
              </div>
              <div className="form-group">
                <label>Duration (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., 5:30"
                  value={video.length}
                  onChange={(e) => setVideo({ ...video, length: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={video.content}
                  onChange={(e) => setVideo({ ...video, content: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Update Video' : 'Add Video')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const QuestionModal = ({ onClose, onAdd, moduleId, isEdit, editingItem }) => {
  const isEditing = !!editingItem;
  const [question, setQuestion] = useState({
    type: 'TrainingQuestion',
    title: editingItem?.title || '',
    content: editingItem?.content || '',
    question_type: editingItem?.question_type || 'true_false',
    options: editingItem?.options || [],
    correct_answer: editingItem?.correct_answer || '',
    points: editingItem?.points || 1
  });
  const [saving, setSaving] = useState(false);

  const handleQuestionTypeChange = (type) => {
    if (type === 'true_false') {
      setQuestion({
        ...question,
        question_type: type,
        options: [],
        correct_answer: ''
      });
    } else {
      setQuestion({
        ...question,
        question_type: type,
        options: question.options.length > 0 ? question.options : ['', ''],
        correct_answer: ''
      });
    }
  };

  const addOption = () => {
    setQuestion({
      ...question,
      options: [...question.options, '']
    });
  };

  const updateOption = (index, value) => {
    const newOptions = [...question.options];
    newOptions[index] = value;
    setQuestion({ ...question, options: newOptions });
  };

  const removeOption = (index) => {
    setQuestion({
      ...question,
      options: question.options.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSaving(true);

    try {
      // If editing an existing module, save to server immediately
      if (isEdit && moduleId) {
        const url = isEditing
          ? `/training_modules/${moduleId}/training_items/${editingItem.id}`
          : `/training_modules/${moduleId}/training_items`;
        const method = isEditing ? 'PATCH' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ training_item: question })
        });

        if (response.ok) {
          window.location.reload();
        } else {
          swal('Error', `Failed to ${isEditing ? 'update' : 'add'} question`, 'error');
        }
      } else {
        // For new modules, just add to local state
        onAdd(question);
      }
    } catch (error) {
      swal('Error', `Error ${isEditing ? 'updating' : 'adding'} question`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditing ? 'Edit Question' : 'Add Question'}</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question Text <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={question.content}
                  onChange={(e) => setQuestion({ ...question, content: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Question Type <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={question.question_type}
                  onChange={(e) => handleQuestionTypeChange(e.target.value)}
                  required
                >
                  <option value="true_false">True/False</option>
                  <option value="multiple_choice">Multiple Choice</option>
                </select>
              </div>

              {question.question_type === 'true_false' ? (
                <div className="form-group">
                  <label>Correct Answer <span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    value={question.correct_answer}
                    onChange={(e) => setQuestion({ ...question, correct_answer: e.target.value })}
                    required
                  >
                    <option value="">Select answer</option>
                    <option value="True">True</option>
                    <option value="False">False</option>
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Answer Options <span className="text-danger">*</span></label>
                  <small className="form-text text-muted mb-2">Click the radio button to mark the correct answer.</small>
                  {question.options.map((option, index) => (
                    <div key={index} className="input-group mb-2">
                      <div className="input-group-prepend">
                        <div className="input-group-text bg-white">
                          <input
                            type="radio"
                            name="correct_answer"
                            checked={question.correct_answer === option && option !== ''}
                            onChange={() => setQuestion({ ...question, correct_answer: option })}
                            title="Mark as correct answer"
                          />
                        </div>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const wasCorrect = question.correct_answer === option;
                          const newOptions = [...question.options];
                          newOptions[index] = newValue;
                          setQuestion({
                            ...question,
                            options: newOptions,
                            correct_answer: wasCorrect ? newValue : question.correct_answer
                          });
                        }}
                        required
                      />
                      {question.options.length > 2 && (
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              const newOptions = question.options.filter((_, i) => i !== index);
                              setQuestion({
                                ...question,
                                options: newOptions,
                                correct_answer: question.correct_answer === option ? '' : question.correct_answer
                              });
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addOption}>
                    <i className="fa fa-plus"></i> Add Option
                  </button>
                  {!question.correct_answer && question.options.some(o => o.trim() !== '') && (
                    <div className="text-danger mt-2">
                      <small><i className="fa fa-exclamation-circle"></i> Please select the correct answer</small>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Point Value <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={question.points}
                  onChange={(e) => setQuestion({ ...question, points: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Update Question' : 'Add Question')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingModuleBuilder;
